import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Lock } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, hasPhysical, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", pincode: "" });
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", mobile: user.mobile || "" }));
  }, [user]);

  useEffect(() => {
    if (!items.length) return;
    api.post("/cart/calculate", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      coupon_code: state?.coupon || null,
    }).then(({ data }) => setTotals(data)).catch(() => {});
    api.post("/events", { event_type: "checkout_start", data: {} }).catch(() => {});
  }, [items, state]);

  if (!items.length) {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center"><h1 className="text-xl font-bold text-navy">Your cart is empty</h1><Button asChild className="mt-4"><Link to="/shop">Browse products</Link></Button></div>;
  }

  const completeOrder = async (orderRes, verifyPayload) => {
    try {
      const { data } = await api.post("/checkout/verify", verifyPayload);
      if (data.status === "success") {
        clear();
        navigate(`/order-success/${orderRes.order_id}`, { replace: true });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const placeOrder = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (hasPhysical && (!addr.line1 || !addr.city || !addr.pincode)) return toast.error("Shipping address is required");
    if (!agreed) return toast.error("Please accept the Terms, Privacy and Refund policies to continue");
    setLoading(true);
    let orderRes;
    try {
      const { data } = await api.post("/checkout/create-order", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        coupon_code: state?.coupon || null, email: form.email, name: form.name, mobile: form.mobile,
        shipping_address: hasPhysical ? { name: form.name, mobile: form.mobile, ...addr } : null,
        policy_consent: true,
      });
      orderRes = data;
    } catch (e) { setLoading(false); return toast.error(formatApiError(e.response?.data?.detail)); }

    const ok = await loadRazorpay();
    if (!ok) { setLoading(false); return toast.error("Could not load payment gateway"); }
    const rzp = new window.Razorpay({
      key: orderRes.key_id, amount: orderRes.amount, currency: orderRes.currency,
      name: "Water Business Guru", description: `Order ${orderRes.order_no}`,
      order_id: orderRes.razorpay_order_id,
      prefill: { name: form.name, email: form.email, contact: form.mobile },
      theme: { color: "#0D9488" },
      handler: (resp) => completeOrder(orderRes, {
        order_id: orderRes.order_id, razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_order_id: resp.razorpay_order_id, razorpay_signature: resp.razorpay_signature,
      }),
      modal: { ondismiss: () => { setLoading(false); toast.message("Payment cancelled. You can try again."); } },
    });
    rzp.on("payment.failed", (resp) => toast.error(resp.error?.description || "Payment failed. Please try again."));
    rzp.open();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-navy mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="font-heading font-semibold text-navy mb-4">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Full name*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" /></div>
              <div><Label className="text-xs">Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" /></div>
              <div><Label className="text-xs">Mobile / WhatsApp</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="checkout-mobile" /></div>
            </div>
            {!user && <p className="text-xs text-slate-400 mt-3">Checking out as guest. <Link to="/login" className="text-teal">Sign in</Link> to save your library.</p>}
          </div>

          {hasPhysical && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="font-heading font-semibold text-navy mb-4">Shipping address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Label className="text-xs">Address*</Label><Input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="checkout-addr-line1" /></div>
                <div><Label className="text-xs">City*</Label><Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="checkout-addr-city" /></div>
                <div><Label className="text-xs">State</Label><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="checkout-addr-state" /></div>
                <div><Label className="text-xs">Pincode*</Label><Input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} data-testid="checkout-addr-pincode" /></div>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 sticky top-20">
            <h2 className="font-heading font-semibold text-navy mb-4">Order summary</h2>
            <div className="space-y-2 max-h-52 overflow-auto no-scrollbar mb-3">
              {(totals?.items || items).map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm">
                  <span className="text-slate-600">{i.name} × {i.quantity}</span>
                  <span className="font-medium">{inr((i.unit_price || i.price) * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{inr(totals?.subtotal ?? subtotal)}</span></div>
              {totals?.discount > 0 && <div className="flex justify-between text-teal"><span>Discount</span><span>-{inr(totals.discount)}</span></div>}
              {totals?.tax > 0 && <div className="flex justify-between"><span className="text-slate-500">Tax</span><span>{inr(totals.tax)}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{totals?.shipping ? inr(totals.shipping) : "Free"}</span></div>
              <div className="border-t border-slate-200 pt-2 flex justify-between text-base font-bold text-navy"><span>Total</span><span data-testid="checkout-total">{inr(totals?.total ?? subtotal)}</span></div>
            </div>
            <label className="flex items-start gap-2 mt-5 text-xs text-slate-600 cursor-pointer" data-testid="checkout-consent-label">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 accent-teal-600" data-testid="checkout-consent" />
              <span>I agree to the <Link to="/terms" target="_blank" className="text-teal underline">Terms &amp; Conditions</Link>, <Link to="/privacy" target="_blank" className="text-teal underline">Privacy Policy</Link> and <Link to="/refund-policy" target="_blank" className="text-teal underline">Refund &amp; Cancellation Policy</Link>.</span>
            </label>
            <Button className="w-full mt-4 bg-teal hover:bg-teal-dark text-white" size="lg" onClick={placeOrder} disabled={loading || !agreed} data-testid="place-order-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1" /> Pay {inr(totals?.total ?? subtotal)}</>}
            </Button>
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1 justify-center"><ShieldCheck className="w-3 h-3" /> Secured by Razorpay</p>
          </div>
        </div>
      </div>
    </div>
  );
}
